import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Logo from "../../../images/logo.png";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../../../components/Loader";
import { sendOtp } from "../../../services/sendOtpService";
import { toast } from "react-toastify";
import { useTheme } from "../../../context/ThemeContext";
import secureStorage from "../../../utils/secureStorage";

function ForgotPassword() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  const handleBack = () => {
    navigate("/login");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      // Send OTP for password reset (try otp_purpose = 1 for password reset)
      const response = await sendOtp(email, 1);

      if (response.success) {
        // Store email and token for the next step
        secureStorage.setItem("reset_email", email);
        if (response.data?.email_token) {
          secureStorage.setItem("email_token", response.data.email_token);
        }

        // Set timers: 60 seconds for OTP validity, resend timer based on response or default
        const otpDuration = 60 * 1000; // 60 seconds for OTP validity
        const resendDuration = response?.data?.email_resend_time
          ? response.data.email_resend_time * 1000
          : 60 * 1000; // Default to 60 seconds if not provided
        secureStorage.setItem("otp_expiration", String(Date.now() + otpDuration));
        secureStorage.setItem("resend_expiration", String(Date.now() + resendDuration));

        toast.success(response.message);

        // Redirect to OTP verification page after 2 seconds
        setTimeout(() => {
          navigate("/otp?type=reset");
        }, 2000);
      } else {
        setError(response.message || "Failed to send OTP. Please try again.");
        toast.error(response.message || "Failed to send OTP");
      }
    } catch (err) {
      setError("Failed to send OTP. Please try again.");
      toast.error("Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0C1C]  px-4 sm:px-6 md:px-10 items-center justify-center">
      <div className="flex flex-col md:flex-row w-full pt-10">
        <div className="flex flex-col w-full items-center justify-center pb-5">
          <div
            className="loginWrapper text-white w-full sm:w-11/12 md:w-2/4 lg:w-2/5 xl:w-2/5 bg-fbfcfc rounded-xl px-5 sm:px-10 py-6 shadow-xl"
            style={{ backgroundColor: isDarkMode ? "#0A0C1C" : "#6D29F6" }}
          >
            {/* Back Button */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handleBack}
                className="flex items-center text-white hover:text-gray-300 transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                {t("back")}
                {/* <span className="text-sm">Back</span> */}
              </button>

              <div></div>
            </div>
            <div className="text-center py-1 flex flex-col gap-2 justify-center items-center">
              <img src="/logo-bg.png" alt="VibeLinks Logo" className="h-16" />
              <h1 className="text-2xl font-bold">
                {t("app_name", "VibeLinks")}
              </h1>
            </div>

            <div className="flex flex-col items-center justify-center text-center mb-4">
              {/* <img src={Logo} alt="Logo" className="w-12 sm:w-14 mt-2" /> */}
              <p className="text-white text-sm pt-8">
                {t(
                  "forgot_password_description",
                  "Forgot your password? No problem! Just enter the email address associated with your account and we'll send you a link to reset your password. Make sure to check your inbox and spam folder"
                )}
              </p>

              <h1 className="text-xl sm:text-2xl font-semibold mt-2 md:hidden">
                {t("forgot_password")}
              </h1>
            </div>
            <form onSubmit={handleSubmit} className="w-full">
              <div className="mb-4">
                <label className="text-[12px] mb-1 text-white">
                  {t("email_address")}
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-4 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-700"
                    style={isDarkMode ? { backgroundColor: '#ffffff', color: '#000000' } : { backgroundColor: '#ffffff', color: '#000000' }}
                    placeholder={t("email_phone")}
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-[80%] mx-auto py-2 rounded-lg font-semibold hover:bg-opacity-90 transition duration-150 flex items-center justify-center"
                style={{
                  backgroundColor: isDarkMode ? '#6d29f6' : 'white',
                  color: isDarkMode ? 'white' : '#7c3aed'
                }}
              >
                {loading ? (
                  <>
                    <Loader loaderColor={isDarkMode ? "white" : "purple-700"} />
                    <span className="ml-2">{t("processing")}...</span>
                  </>
                ) : (
                  t("reset_password")
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
